/**
 * The one shimmering line under a running turn, Cursor's "Planning next
 * moves": what the agent is doing right now, in words. Read off the stream,
 * not a spinner in the panel chrome:
 *
 * - a `data-status` part that is the message's last part ("Reading the case",
 *   "Choosing a model", "Filling the form"), else
 * - the last tool call while it runs ("Reading I-129.pdf"), else
 * - "Thinking" while reasoning streams, "Planning next moves" while text
 *   does, and "Thinking" again between a tool's result and the model's next
 *   move.
 *
 * Nothing renders once the turn is over.
 */
import { Square } from "lucide-react";
import { memo, type FC } from "react";

import { Shimmer } from "@/components/ai-elements/shimmer";
import { Button } from "@/components/ui/button";
import type {
  AgentUIMessage,
  TurnStatusData
} from "@/features/agents/useAgentChat";
import { cn } from "@/lib/utils";

import { toolNameOf, type AnyToolPart } from "./ToolCard";
import { useToolTitle } from "./ToolRow";

type Part = AgentUIMessage["parts"][number];

const isToolPart = (p: Part): p is AnyToolPart =>
  p.type === "dynamic-tool" || p.type.startsWith("tool-");

const isStatusPart = (
  p: Part
): p is { type: "data-status"; id?: string; data: TurnStatusData } =>
  p.type === "data-status";

/** The message's last part that says something about what is happening. */
const lastTelling = (parts: readonly Part[]): Part | null => {
  for (let i = parts.length - 1; i >= 0; i -= 1) {
    const p = parts[i];
    if (p.type !== "step-start" && p.type !== "data-model") return p;
  }
  return null;
};

export type TurnStatusView =
  | { kind: "status"; label: string }
  | { kind: "tool"; part: AnyToolPart }
  | { kind: "text"; label: "Planning next moves" | "Thinking" };

/** What the line should say for a streaming message (null: no message yet). */
export const turnStatusView = (
  message: Pick<AgentUIMessage, "parts"> | null | undefined
): TurnStatusView => {
  const last = message ? lastTelling(message.parts) : null;
  if (!last) return { kind: "text", label: "Planning next moves" };
  if (isStatusPart(last)) {
    const label = last.data?.label?.trim();
    if (label) return { kind: "status", label };
  }
  if (isToolPart(last)) {
    if (last.state === "input-streaming" || last.state === "input-available")
      return { kind: "tool", part: last };
    // Its result is in; the model is deciding what to do next.
    return { kind: "text", label: "Thinking" };
  }
  if (last.type === "reasoning") return { kind: "text", label: "Thinking" };
  return { kind: "text", label: "Planning next moves" };
};

const Label: FC<{ text: string }> = ({ text }) => (
  <Shimmer as="span" className="text-sm" duration={1.6}>
    {text}
  </Shimmer>
);

const ToolLabel: FC<{ part: AnyToolPart }> = ({ part }) => {
  const title = useToolTitle(toolNameOf(part), part.input);
  return <Label text={title.present} />;
};

type Props = {
  /** The assistant message being streamed (none yet while `submitted`). */
  message: Pick<AgentUIMessage, "parts"> | null | undefined;
  /** Shown when the turn can be stopped from here. */
  onStop?: () => void;
  className?: string;
};

export const TurnStatusLine: FC<Props> = memo(
  ({ message, onStop, className }) => {
    const view = turnStatusView(message);
    return (
      <div
        className={cn(
          "group/turn flex h-6 items-center gap-1.5 pr-1 text-sm text-muted-foreground",
          className
        )}
        data-testid="turn-status"
        data-kind={view.kind}
        role="status"
        aria-live="polite"
      >
        {view.kind === "tool" ? (
          <ToolLabel part={view.part} />
        ) : (
          <Label text={view.label} />
        )}
        {onStop ? (
          <Button
            variant="ghost"
            size="xs"
            className="ml-auto h-5 gap-1 px-1.5 text-xs text-muted-foreground opacity-0 transition-opacity hover:text-foreground group-hover/turn:opacity-100 focus-visible:opacity-100"
            onClick={onStop}
            data-testid="turn-status-stop"
          >
            <Square className="size-2.5 fill-current" /> Stop
          </Button>
        ) : null}
      </div>
    );
  }
);
TurnStatusLine.displayName = "TurnStatusLine";
